import { camelToSnake, snakeToCamel } from "./legacyConvert";

const camelKeyRegex = /([a-z])([A-Z0-9])/g;
const camelKeyRegex2 = /([0-9])([a-zA-Z])/g;
const nonWordAndHyphenRegex = /([^a-zA-Z0-9-])+/g;
const pascalCaseRegex = /([a-z])([A-Z])/g
const multipleUpperCaseFollowedByUpperCaseAndLowerCaseRegex = /([A-Z]+)([A-Z][a-z])/g;
const multipleNumbersFollowedByWordRegex = /([0-9]+)([^0-9 -_])/g;
const startOrEndWithHyphenRegex = /^-|-$/g;


/**
 * The function capitalizes the string's first letter.
 * @param {string} str
 * @returns {string}
 * 
 * @example
 * capitalize('string') // returns 'String'
 */
export function capitalize(value){
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

/**
 * The function removes colons from the string.
 * @param {string} str
 * @returns {string}
 * 
 * @example
 * colonless('st:ri:ng') // returns 'string'
 */
export function colonless(value){
  const strRegex = /:+\s*$/;
  return (value || '').replace(strRegex, '');
}

/**
 * Converts a string from camelCase to a sentance (a normal text with spaces between words and also capitalize the first word).
 *
 * @param {string} value camelCase
 * @param {boolean} titleize Flag to indicate if all words are required to be capitalize
 * @returns {string}
 *
 * @example
 * camelToText('helloWorld')
 * // returns 'Hello world'
 *
 * camelToText('helloWorld', true)
 * // returns 'Hello World'
 */
export function camelToText(value, titleize) {
  const replacement = (match, first, second) => `${first} ${second}`;

  const result = value
    .toString()
    .replace(camelKeyRegex, replacement)
    .replace(camelKeyRegex2, replacement)
    .replace(/-/g, ' ')
    .toLowerCase();

  if (titleize) {
    return _.titleize(result);
  }

  return capitalize(result);
}

/**
 * Transforms a date to moment object type
 * @param {Date} date The date that should be converted
 * @param {string} key The key
 * @returns {string} The date converted to moment
 */
export function transformDate(date, key) {
  if (!date) {
    return null;
  }

  // Datetime with timezone.
  let d = moment(date, 'YYYY-MM-DDTHH:mm:ssZZ', true);
  if (d.isValid()) {
    return d;
  }

  // Datetime without timezone.
  d = moment(date, 'YYYY-MM-DDTHH:mm:ss', true);
  if (d.isValid()) {
    return d;
  }

  // Date.
  d = moment(date, 'YYYY-MM-DD', true);
  if (d.isValid()) {
    return d;
  }

  // Allow non-strings to just be returned.
  if (!_.isString(date)) {
    return date;
  }

  // Edge case in which the year is 3 digits. Strict moment parsing does not support that
  // so zero-pad the year to make it 4 digits.
  if (date.split('-')[0].length === 3) {
    console.warn('convert: date has a 3-digit year; please fix.', key, date);

    const dateCopy = '0'.concat(date);

    return transformDate(dateCopy, key);
  }

  // Don't throw: one bad nested value would reject the whole response.
  console.warn(`convert: invalid date ${key}: ${date}`);
  return date;
}

/**
 * Transform the given 'data' from server-side format (snake_case)
 * to client-side format (camelCase). Also replaces date strings with
 * real Date instances.
 * @param {Any} data The data that should be transformed
 * @param {string} key The key
 * @returns {Object} The transformed object
 */
export function transformServerResponse(data) {
  const atRegex = /_at$/;

  if (_.isArray(data)) {
    for (let i = 0, len = data.length; i < len; i += 1) {
      transformServerResponse(data[i]);
    }
  } else if (_.isObject(data)) {
    Object.keys(data).forEach((key) => {
      const value = data[key];
      delete data[key];

      const newKey = snakeToCamel(key);

      // HACK: Convert dates to JS dates by noting that
      // all of our dates are in fields ending with "_at",
      // or are exactly "at".
      if (atRegex.test(key) || key === 'at') {
        data[newKey] = transformDate(value, newKey);
      } else {
        data[newKey] = transformServerResponse(value);
      }
    });
  }

  return data;
}

/**
 * Transform client request (camelCase) to the format (snake_case)
 * understood by server.
 *
 * NOTE: we can't reuse data because someone else might
 * be retaining a reference to it!
 * @param {Any} data The data that should be transformed
 * @returns {Object} The transformed object
 */
export function transformClientRequest(data) {
  let response;
  let newKey;

  if (moment.isMoment(data)) {
    response = data.format();
  } else if (_.isDate(data)) {
    throw new Error(`convert: expected moment, not date ${data}`);
  } else if (_.isArray(data)) {
    response = [];
    for (let i = 0, len = data.length; i < len; i += 1) {
      response.push(transformClientRequest(data[i]));
    }
  } else if (_.isObject(data)) {
    response = {};
    Object.keys(data).forEach((key) => {
      // Ignore properties prefixed with `$` as they are supposed to be internal.
      if (key && key[0] !== '$') {
        newKey = camelToSnake(key);
        response[newKey] = transformClientRequest(data[key]);
      }
    });
  } else {
    response = data;
  }
  return response;
}


/**
 * The function transform string to snake_case.
 * @param {string} value
 * @returns {string}
 * 
 * @example
 * toSnakeCase('fromCamelCase') // returns 'from_camel_case'
 * toSnakeCase('FromPascalCase') // returns 'from_pascal_case'
 * toSnakeCase('from_snake_case') // returns 'from_snake_case'
 * toSnakeCase('!Multiple  spaces and@#$ non-words!') // returns 'multiple_spaces_and_non_words'
 * toSnakeCase('123multipleNumbers') // returns '123_multiple_numbers'
 */
export function toSnakeCase(value = '') { 
  return value?.replace(pascalCaseRegex, '$1_$2') // Handle PascalCase
  .replace(multipleUpperCaseFollowedByUpperCaseAndLowerCaseRegex, '$1-$2') // Handle multiple uppercase letters followed by uppercase letter and lowercase letter
  .replace(multipleNumbersFollowedByWordRegex, '$1-$2') // Handle numbers
  .replace(nonWordAndHyphenRegex, '_') // Handle any consecutive non-word and hyphens  
  .replace(/^_|_$/g, '') // Handle first or last character to be underscore
  .replace(/-/g, '_') // Remove hyphens and concat word according to snake_case rule
  .toLowerCase();
}

/**
 * The function transform string to kebab-case.
 * @param {string} value
 * @returns {string}
 * 
 * @example
 * toKebabCase('fromCamelCase') // returns 'from-camel-case'
 * toKebabCase('FromPascalCase') // returns 'from-pascal-case'
 * toKebabCase('from_snake_case') // return 'from-snake-case'
 * toKebabCase('!Multiple  spaces and@#$ non-words!') // returns 'multiple-spaces-and-non-words'
 * toKebabCase('123multipleNumbers') // returns '123-multiple-numbers'
 */
export function toKebabCase(value = '') { 
  return value?.replace(pascalCaseRegex, '$1-$2') // Handle PascalCase
  .replace(multipleUpperCaseFollowedByUpperCaseAndLowerCaseRegex, '$1-$2') // Handle multiple uppercase letters followed by uppercase letter and lowercase letter
  .replace(multipleNumbersFollowedByWordRegex, '$1-$2') // Handle numbers
  .replace(nonWordAndHyphenRegex, '-') // Handle any consecutive non-word and hyphens  
  .replace(startOrEndWithHyphenRegex, '') // Handle first or last character to be hyphen
  .toLowerCase();
}

/**
 * The function transform string to camelCase.
 * @param {string} value
 * @returns {string}
 * 
 * @example
 * toCamelCase('fromCamelCase') // returns 'fromCamelCase'
 * toCamelCase('FromPascalCase') // returns 'fromPascalCase'
 * toCamelCase('from_snake_case') // returns 'fromSnakeCase'
 * toCamelCase('!Multiple  spaces and@#$ non-words!') // returns 'multipleSpacesAndNonWords'
 * toCamelCase('123multipleNumbers') // returns '123MultipleNumbers'
 */
export function toCamelCase(value = '') { 
  return value?.replace(nonWordAndHyphenRegex, '-') // Handle any consecutive non-word and hyphens  
  .replace(multipleUpperCaseFollowedByUpperCaseAndLowerCaseRegex, '$1-$2') // Handle multiple uppercase letters followed by uppercase letter and lowercase letter
  .replace(multipleNumbersFollowedByWordRegex, '$1-$2') // Handle numbers
  .replace(pascalCaseRegex, '$1-$2') // Handle PascalCase
  .replace(startOrEndWithHyphenRegex, '') // Remove first or last character if hyphen
  .replace(/([a-zA-Z0-9]+)([_-]|$)/g,(_,match)=> capitalize(match)) // Remove hyphens and concat word according to camelCase rule
  .replace(/^[A-Z]/, (match)=> match.toLowerCase()); // Transform first char to lowercase.
}

/**
 * The function transform string to PascalCase
 * @param {string} value
 * @returns {string}
 * 
 * @example
 * toPascalCase('fromCamelCase') // returns 'FromCamelCase'
 * toPascalCase('FromPascalCase') // returns 'FromPascalCase'
 * toPascalCase('from_snake_case') // returns 'FromSnakeCase'
 * toPascalCase('!Multiple  spaces and@#$ non-words!') // returns 'MultipleSpacesAndNonWords'
 * toPascalCase('123multipleNumbers') // returns '123MultipleNumbers'
 */
export function toPascalCase(value = '') { 
  return value?.replace(nonWordAndHyphenRegex, '-') // Handle any consecutive non-word and hyphens  
  .replace(multipleUpperCaseFollowedByUpperCaseAndLowerCaseRegex, '$1-$2') // Handle multiple uppercase letters followed by uppercase letter and lowercase letter
  .replace(multipleNumbersFollowedByWordRegex, '$1-$2') // Handle numbers
  .replace(pascalCaseRegex, '$1-$2') // Handle PascalCase
  .replace(startOrEndWithHyphenRegex, '') // Handle first or last character to be hyphen
  .replace(/([a-zA-Z0-9]+)([-]|$)/g,(_,match)=> capitalize(match)) // Remove hyphens and concat word according to camelCase rule
}